import type { DiscussionVisibility } from 'types/discussion'

export const DISCUSSIONS_CONTEXT = 'Discussions'

export const DISCUSSION_EVENTS = {
  created: 'Discussion created',
  edited: 'Discussion edited',
  replied: 'Discussion replied',
  deleted: 'Discussion deleted',
  reaction: 'Discussion reaction',
  timestampClicked: 'Discussion timestamp clicked',
  notificationSettingChanged: 'Discussion notification setting changed',
  actionFailed: 'Discussion action failed',
  opened: 'Discussions opened',
} as const

export type DiscussionEventTarget = {
  target_type: string
  // Optional: a bulk create spans many objects
  target_object_id?: number | string
}

export type DiscussionEventContext = DiscussionEventTarget & {
  visibility: DiscussionVisibility
}

// Add an entry here for each new object type that gets discussions
const TARGET_IDENTITY: Record<string, { type: string; idKey: string }> = {
  track: { type: 'Track', idKey: 'Track ID' },
}

/** Maps a discussion target to its Mixpanel identity props (e.g. `Type`, `Track ID`). */
export const getDiscussionTargetProps = ({
  target_type,
  target_object_id,
}: DiscussionEventTarget): Record<string, number | string> => {
  const identity = TARGET_IDENTITY[target_type]
  if (!identity) return {}
  return {
    ...(target_object_id !== undefined
      ? { [identity.idKey]: target_object_id }
      : {}),
    Type: identity.type,
  }
}

/** Builds the props for a discussion event. Caller props win. */
export const buildDiscussionEventProps = (
  { visibility, ...target }: DiscussionEventContext,
  props: Record<string, unknown> = {},
): Record<string, unknown> => ({
  ...getDiscussionTargetProps(target),
  Context: DISCUSSIONS_CONTEXT,
  visibility,
  ...props,
})

export type NotificationSettingSource = 'notes_tab' | 'settings_page'

const NOTIFICATION_SETTING_CONTEXT: Record<NotificationSettingSource, string> =
  {
    notes_tab: DISCUSSIONS_CONTEXT,
    settings_page: 'Notification settings',
  }

export type NotificationSettingChange = {
  setting: string
  previous_value: unknown
  new_value: unknown
}

/**
 * Builds the props for a notification setting event. Only the per-track bell
 * passes a target, because the settings page is account-wide.
 */
export const buildNotificationSettingEventProps = (
  source: NotificationSettingSource,
  change: NotificationSettingChange,
  target?: DiscussionEventTarget,
): Record<string, unknown> => ({
  ...(target ? getDiscussionTargetProps(target) : {}),
  Context: NOTIFICATION_SETTING_CONTEXT[source],
  ...change,
})

/** Extracts a numeric HTTP status from an RTK Query error, if present. */
export const getErrorStatus = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return typeof status === 'number' ? status : undefined
}

/** Minimal subset of TipTap's JSONContent, so this package needs no TipTap dependency. */
export type ContentNode = {
  type?: string
  text?: string
  content?: ContentNode[]
}

export type ContentCounts = {
  mention_count: number
  timestamp_count: number
  timestamp_range_count: number
}

type TimestampMatch = { end: unknown }

type FindTimestamps = (text: string) => TimestampMatch[]

const ZERO_COUNTS: ContentCounts = {
  mention_count: 0,
  timestamp_count: 0,
  timestamp_range_count: 0,
}

const addCounts = (a: ContentCounts, b: ContentCounts): ContentCounts => ({
  mention_count: a.mention_count + b.mention_count,
  timestamp_count: a.timestamp_count + b.timestamp_count,
  timestamp_range_count: a.timestamp_range_count + b.timestamp_range_count,
})

const isTextBlock = (node: ContentNode): boolean =>
  (node.content ?? []).some(child => child.type === 'text')

/** Counts mentions and timestamps in a TipTap JSON tree. */
export const countDiscussionContent = (
  node: ContentNode,
  findTimestamps: FindTimestamps,
): ContentCounts => {
  let own = ZERO_COUNTS
  if (isTextBlock(node)) {
    const matches = (node.content ?? [])
      .filter(child => child.type === 'text')
      .flatMap(child => findTimestamps(child.text ?? ''))
    own = {
      mention_count: 0,
      timestamp_count: matches.length,
      timestamp_range_count: matches.filter(m => m.end !== null).length,
    }
  }

  const withMention: ContentCounts = {
    ...own,
    mention_count: own.mention_count + (node.type === 'mention' ? 1 : 0),
  }

  return (node.content ?? [])
    .map(child => countDiscussionContent(child, findTimestamps))
    .reduce(addCounts, withMention)
}
